import { useAnalytics } from '@oriola-corporation/kronan-analytics';
import clsx from 'clsx';
import React, {
  useCallback,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  PropsWithChildren,
  useState,
} from 'react';
import { AnalyticsEvents, MODAL_OPENED } from './analytics';
import { ModalAction, ModalContent } from './ModalContent';

export interface ModalProps {
  fullScreen?: boolean;
  title: string;
  closeLabel?: string;
  titleIcon?: {
    component: React.ReactNode;
  };
  actions?: Array<ModalAction | undefined | false | null>;
  subtitle?: string;
  showCloseButton?: boolean;
  isWithoutContentPadding?: boolean;
  onClose?: () => void;
  id: string;
}

export type ModalRef = {
  openModal: () => void;
  closeModal: () => void;
  isModalOpen: () => boolean | undefined;
};

/**
 * Modal is a component that provides a modal dialog with a title, subtitle, actions, and a close button. \
 * Use it by setting a ref on the component and calling the openModal method.
 *
 * @example
 * ```tsx
 * import { useRef } from 'react';
 * import { Modal, ModalRef } from './Modal';
 *
 * const Example = () => {
 *  const modalRef = useRef<ModalRef>(null);
 *  return (
 *    <div>
 *      <Modal ref={modalRef} title="Modal Title" id="example-modal">
 *        <p>This is the modal content.</p>
 *      </Modal>
 *      <button onClick={() => modalRef.current?.openModal()}>Open Modal</button>
 *      <button onClick={() => modalRef.current?.closeModal()}>Close Modal</button>
 *    </div>
 *  )
 * }
 * ```
 */
export const Modal = forwardRef<ModalRef, PropsWithChildren<ModalProps>>(
  (
    {
      fullScreen = false,
      title,
      closeLabel = 'Stäng',
      children,
      titleIcon,
      actions = [],
      subtitle,
      showCloseButton = true,
      isWithoutContentPadding = false,
      onClose,
      id,
    },
    ref
  ) => {
    const { trackEvent } = useAnalytics<AnalyticsEvents>();
    const dialogRef = useRef<HTMLDialogElement | null>(null);
    // This state handles the visibility of the dialog content, hiding it in the DOM while the dialog is closed (has display: none).
    // It prevents the heading from being read out by Gatsby Announcer. It also possible optimizes SEO on the site.
    const [isVisibleDialog, setIsVisibleDialog] = useState(false);

    useImperativeHandle(ref, () => ({
      openModal: () => {
        dialogRef.current?.showModal();
        setIsVisibleDialog(true);
        trackEvent({
          type: MODAL_OPENED,
          payload: {
            title,
          },
        });
      },
      closeModal: () => {
        dialogRef.current?.close();
        setIsVisibleDialog(false);
      },
      isModalOpen: () => dialogRef.current?.open,
    }));

    useEffect(() => {
      if (dialogRef.current) {
        const dialog = dialogRef.current;
        const observer = new MutationObserver(() => {
          const isDialogOpen = dialog.open;
          if (isDialogOpen) {
            document.body.style.overflow = 'hidden';
          } else {
            document.body.style.overflow = '';
          }
        });
        observer.observe(dialog, {
          attributes: true,
          attributeFilter: ['open'],
        });
        return () => {
          document.body.style.overflow = '';
          observer.disconnect();
        };
      }
    }, []);

    const closeOnOutsideClick = useCallback((e: MouseEvent) => {
      if (dialogRef && dialogRef.current && e.target === dialogRef.current) {
        dialogRef.current.close();
      }
    }, []);

    useEffect(() => {
      const dialogElement = dialogRef.current;
      if (dialogElement) {
        dialogElement?.addEventListener('click', closeOnOutsideClick);
      }
      return () => {
        dialogElement?.removeEventListener('click', closeOnOutsideClick);
      };
    }, [closeOnOutsideClick]);

    return (
      <div
        className={clsx('max-w-full', {
          'w-full': fullScreen,
          'max-w-[31.25rem]': !fullScreen,
          'px-4': !fullScreen && isVisibleDialog,
        })}
      >
        <dialog
          // onClose is the close event of the dialog element. This is triggered by close().
          // When this is triggered, setIsVisibleDialog is run as well as other functions we have provided with the onClose prop.
          onClose={() => {
            if (!dialogRef.current?.open) {
              setIsVisibleDialog(false);
            }
            if (onClose) {
              onClose();
            }
          }}
          id={id}
          ref={dialogRef}
          className={clsx(
            'fixed inset-0 mx-auto w-full overflow-auto bg-white p-0',
            'max-h-[calc(100vh-110px)] max-w-[31.25rem] animate-fade-in',
            {
              'h-full max-h-full sm:max-h-[calc(100vh-110px)] rounded-none sm:rounded-2xl':
                fullScreen,
              'rounded-2xl': !fullScreen,
            },
            'backdrop:bg-black/30 backdrop:backdrop-blur-sm'
          )}
        >
          {isVisibleDialog && (
            <ModalContent
              id={id}
              title={title}
              closeLabel={closeLabel}
              onClose={() => dialogRef.current?.close()}
              titleIcon={titleIcon}
              actions={actions}
              subtitle={subtitle}
              showCloseButton={showCloseButton}
              isWithoutContentPadding={isWithoutContentPadding}
            >
              {children}
            </ModalContent>
          )}
        </dialog>
      </div>
    );
  }
);

Modal.displayName = 'Modal';
